import React, {useCallback, useEffect, useMemo, useState, useRef} from 'react';
import { useParams } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { setCurrentCategory, setCurrentImage } from '../store/slices/categoriesSlice';
import { setColor, reset } from '../store/slices/coloringSlice';
import SVGRenderer from '../components/SVGRenderer';
import ColorPalette from '../components/ColorPalette';
import ColoringHeader from '../components/ColoringHeader';
import CompletionCounter from '../components/CompletionCounter';
import { useGetImageQuery } from '../store/api/imagesApi';
import ResetDialog from '../components/ResetDialog';
import ZoomableCanvas from '../components/ZoomableCanvas';
import {FOOTER_HEIGHT} from '../styles/constants';
import { 
  selectCategories, 
  selectCurrentCategory,
  makeSelectImageFromCurrentCategory
} from '../store/selectors/categoriesSelectors';
import {
  selectCurrentColor,
  selectHistory,
  selectCurrentStep,
  makeSelectCurrentColorIndex
} from '../store/selectors/coloringSelectors';
import { trackPageView, trackColoringStart, trackColoringComplete, trackUserInteraction } from '../utils/analytics';
import {Theme} from "../config/theme";

const GlobalStyle = createGlobalStyle`
  * {
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
  }

  body {
    -webkit-touch-callout: none;
  }
`;

const ColoringContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  height: -webkit-fill-available;
  background: transparent;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  &.coloring-page {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    overflow: hidden;
  }
`;

// const CanvasWrapper = styled.div`
//   flex: 1;
//   display: flex;
//   justify-content: center;
//   align-items: center;
//   padding: 1rem;
//   overflow: hidden;
//   position: relative;
//   min-height: 0;
// `;

// const CanvasContainer = styled.div`
//   width: 100%;
//   height: 100%;
//   display: flex;
//   justify-content: center;
//   align-items: center;
//   position: relative;
//   min-height: 0;
//   min-width: 0;
//   transform-origin: center center;
//   transition: transform 0.1s ease-out;
//   will-change: transform;
// `;

const Footer = styled.div`
  flex: none;
  flex-direction: column;
  height: ${FOOTER_HEIGHT} px;
  background: white;
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 2;
  padding-bottom: env(safe-area-inset-bottom);
`;



const Toast = styled.div<{ show: boolean }>`
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 10px 20px;
  border-radius: 8px;
  z-index: 1000;
  opacity: ${props => props.show ? 1 : 0};
  transition: opacity 0.3s ease;
`;

const Loading = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  font-size: 1.5rem;
  color: ${Theme.colors.main};
`;

// Add a type for completed colors
interface CompletedColors {
  [colorIdx: string]: boolean;
}

const Coloring: React.FC = () => {
  const { categoryId, imageId } = useParams<{ categoryId: string; imageId: string }>();
  // const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [isLoadingCategory, setIsLoadingCategory] = useState(false);
  const [remainingCount, setRemainingCount] = useState<number>(0);
  const [completedColors, setCompletedColors] = useState<CompletedColors>({});
  const [allColorsCompleted, setAllColorsCompleted] = useState<boolean>(false);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const [startTime, setStartTime] = useState<number>(Date.now());
  
  // Create selector instances
  const selectImageFromCurrentCategory = useMemo(
    () => makeSelectImageFromCurrentCategory(),
    []
  );
  
  // Replace direct state access with memoized selectors
  const currentColor = useSelector(selectCurrentColor);
  const currentCategory = useSelector(selectCurrentCategory);
  const categories = useSelector(selectCategories);
  const { data: svgContent, isLoading: isLoadingSvg } = useGetImageQuery(imageId || '', {
    skip: !imageId,
    refetchOnMountOrArgChange: true
  });
  const history = useSelector(selectHistory);
  const currentStep = useSelector(selectCurrentStep);
  
  // Get the current image using the memoized selector
  const currentImage = useSelector((state) => 
    selectImageFromCurrentCategory(state, imageId || '')
  );
  
  const showToastMessage = useCallback((message: string) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 2000);
  }, []);

  // const handleBackClick = useCallback(() => {
  //   navigate('/');
  // }, [navigate]);

  const handleResetRequest = useCallback(() => {
    trackUserInteraction('reset_request', {
      category_id: categoryId,
      image_id: imageId
    });
    setShowResetDialog(true);
  }, [categoryId, imageId]);

  const handleResetConfirm = useCallback(() => {
    trackUserInteraction('reset_confirm', {
      category_id: categoryId,
      image_id: imageId
    });
    dispatch(reset());
    setShowResetDialog(false);
    showToastMessage('Drawing reset');
  }, [dispatch, showToastMessage, categoryId, imageId]);

  const handleResetCancel = useCallback(() => {
    setShowResetDialog(false);
  }, []);

  useEffect(() => {
    const loadCategory = async () => {
      if (!categoryId) {
        return;
      }

      setIsLoadingCategory(true);
      try {
        // Fetch category data and set it in Redux
        if (categories && categories.length) {
          const category = categories.find(c => c.id === categoryId);
          if (category) {
            dispatch(setCurrentCategory(category));
          }
        }
      } catch (error) {
        console.error('Failed to load category:', error);
      } finally {
        setIsLoadingCategory(false);
      }
    };

    loadCategory();
  }, [categoryId, categories, dispatch]);

  useEffect(() => {
    if (!imageId || !currentCategory) {
      return;
    }
    
    try {
      const imageFound = currentCategory.images.find(image => image.id === imageId);
      if (imageFound) {
        dispatch(setCurrentImage(imageFound));
      }
    } catch (error) {
      console.error('Failed to load image:', error);
    }
  }, [imageId, currentCategory, dispatch]);

  useEffect(()=>{
    if(!currentImage?.colorScheme?.length){
      return;
    }
    const color = currentImage.colorScheme[0];
    dispatch(setColor(color));
  }, [currentImage, dispatch]);

  // Create the current color index selector
  const selectCurrentColorIndex = useMemo(
    () => makeSelectCurrentColorIndex(),
    []
  );
  
  // Use the memoized selector for current color index
  const currentColorIdx = useSelector((state) => 
    selectCurrentColorIndex(state, currentImage.colorScheme)
  );
  
  const onColorSelected = useCallback((color: string, id: string)=>{
    dispatch(setColor(color));
    
    // Dispatch a color selection event to trigger blinking effect
    const colorSelectedEvent = new CustomEvent('colorSelected', {
      detail: { colorIdx: id }
    });
    window.dispatchEvent(colorSelectedEvent);
  }, [dispatch]);

  // Memoize the color-by-numbers check
  const isColorByNumbers = useMemo(() => {
    return currentImage.colorScheme !== undefined;
  }, [currentImage.colorScheme]);

  // Get remaining count and completed colors from window object
  useEffect(() => {
    const intervalId = setInterval(() => {
      // @ts-ignore
      if (window._remainingCount !== undefined) {
        // @ts-ignore
        setRemainingCount(window._remainingCount);
      }
      
      // @ts-ignore
      if (window._completedColors) {
        // @ts-ignore
        setCompletedColors(window._completedColors);
      }
      
      // @ts-ignore
      if (window._allColorsCompleted !== undefined) {
        // @ts-ignore
        setAllColorsCompleted(window._allColorsCompleted);
      }
    }, 300);
    
    return () => clearInterval(intervalId);
  }, []);

  // Listen for color completion events
  useEffect(() => {
    const handleColorCompleted = (event: CustomEvent<any>) => {
      const { completedColors, allCompleted } = event.detail;
      setCompletedColors(completedColors);
      setAllColorsCompleted(allCompleted);
    };

    const handleAllColorsCompleted = () => {
      setAllColorsCompleted(true);
    };

    window.addEventListener('colorCompleted', handleColorCompleted as EventListener);
    window.addEventListener('allColorsCompleted', handleAllColorsCompleted);

    return () => {
      window.removeEventListener('colorCompleted', handleColorCompleted as EventListener);
      window.removeEventListener('allColorsCompleted', handleAllColorsCompleted);
    };
  }, []);

  // Create a memoized selector for SVG renderer props
  const svgRendererProps = useMemo(() => {
    if(!svgContent || !currentImage) {
      return null;
    }
    
    return {
      svgContent,
      imageId: imageId || "",
      highlightedColorIdx: isColorByNumbers ? `${currentColorIdx}` : undefined
    };
  }, [svgContent, imageId, currentImage, isColorByNumbers, currentColorIdx]);

  const svgRenderer = useMemo(() => {
    if (!svgRendererProps) {
      return null;
    }
    return <SVGRenderer 
      svgContent={svgRendererProps.svgContent}
      imageId={svgRendererProps.imageId}
      highlightedColorIdx={svgRendererProps.highlightedColorIdx}
    />;
  }, [svgRendererProps]);

  // Memoize the loading state calculation
  const isLoading = useMemo(() => {
    return isLoadingCategory || isLoadingSvg || !svgContent;
  }, [isLoadingCategory, isLoadingSvg, svgContent]);

  // Track page view and coloring start
  useEffect(() => {
    if (categoryId && imageId && currentCategory) {
      trackPageView(`Coloring_${imageId}`, {
        category_id: categoryId,
        image_id: imageId,
        category_name: currentCategory.name,
        route_path: `/coloring/${categoryId}/${imageId}`
      });
      
      trackColoringStart(categoryId, imageId);
      setStartTime(Date.now());
    }
  }, [categoryId, imageId, currentCategory]);

  // Track completion
  useEffect(() => {
    if (allColorsCompleted && categoryId && imageId) {
      const timeSpent = Math.round((Date.now() - startTime) / 1000);
      trackColoringComplete(categoryId, imageId, timeSpent);
    }
  }, [allColorsCompleted, categoryId, imageId, startTime]);

  if (isLoading) {
    return <Loading>Loading...</Loading>;
  }

  return (
    <>
      <GlobalStyle />
      <ColoringContainer className="coloring-page" ref={canvasContainerRef}>
        <ColoringHeader
          currentImage={currentImage}
          currentStep={currentStep}
          historyLength={history.length}
          onShowToast={showToastMessage}
          onResetRequest={handleResetRequest}
        />

        <ZoomableCanvas>
          {/*<SVGRenderer*/}
          {/*  svgContent={svgContent}*/}
          {/*  imageId={imageId || ""}*/}
          {/*  highlightedColorIdx={isColorByNumbers ? `${currentColorIdx}` : undefined}*/}
          {/*/>*/}
          {svgRenderer}
        </ZoomableCanvas>

        <Footer>
          <ColorPalette
            selectedColor={currentColor}
            colorScheme={currentImage.colorScheme}
            onColorSelect={onColorSelected}
            completedColors={completedColors}
          />
          <CompletionCounter
            remainingCount={remainingCount}
            isAllCompleted={allColorsCompleted}
          />
        </Footer>

        <Toast show={showToast}>{toastMessage}</Toast>

        {showResetDialog && (
          <ResetDialog
            onConfirm={handleResetConfirm}
            onCancel={handleResetCancel}
          />
        )}
      </ColoringContainer>
    </>
  );
};

export default Coloring;
